import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { User } from "lucide-react";
import { EASE_LUXE } from "./primitives";
import { useLanguage } from "@/i18n/LanguageContext";

const SCREEN_IDS = ["identity", "custody", "condition", "continuity"];
const MAX_AVATARS = 6;

// The shared fmtDate() is locked to fr-FR; this preview is bilingual, so it
// formats against the reader's language instead.
const fmtDate = (iso, language) => {
  try {
    return new Date(iso).toLocaleDateString(language === "en" ? "en-GB" : "fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const ScreenTitle = ({ children }) => (
  <p className="text-[10px] tracking-[0.3em] uppercase text-gold-ink">{children}</p>
);

const Identity = ({ identity, t }) => {
  const rows = ["name", "maker", "year", "material", "reference"].filter(
    (k) => identity[k]
  );
  return (
    <div className="flex h-full flex-col">
      <ScreenTitle>{t("passportPreview.screens.identity")}</ScreenTitle>
      <dl className="mt-5 flex-1">
        {rows.map((key) => (
          <div key={key} className="border-b border-ink/5 py-2.5">
            <dt className="text-[9px] tracking-[0.22em] uppercase text-ink-subtle">
              {t(`passportPreview.identity.${key}`)}
            </dt>
            <dd className="mt-0.5 font-serif text-[15px] leading-snug text-ink-soft">
              {identity[key]}
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 border border-gold/25 bg-gold/5 px-3 py-2 text-[10px] leading-relaxed text-gold-ink">
        {t("passportPreview.identity.noIssuer")}
      </p>
    </div>
  );
};

// Only the actor's *type* is ever shown — never a name. Same rule as the public
// provenance timeline.
const Custody = ({ custody, t, language }) => (
  <div className="flex h-full flex-col">
    <ScreenTitle>{t("passportPreview.screens.custody")}</ScreenTitle>
    <ol className="mt-5 flex-1">
      {custody.map((event, i) => {
        const typeKey = `prov.events.${event.event_type}`;
        const typeLabel = t(typeKey);
        const actorKey = `timeline.actors.${event.actor_type}`;
        const actorLabel = t(actorKey);
        const last = i === custody.length - 1;
        return (
          <li key={event.id} className="relative pl-6 pb-4 last:pb-0">
            {!last && (
              <span className="absolute left-[3px] top-3 bottom-0 w-px bg-ink/10" />
            )}
            <span className="absolute left-0 top-1.5 h-[7px] w-[7px] rounded-full border border-gold/70 bg-surface-raised" />
            <p className="text-[9px] tracking-[0.18em] uppercase text-ink-subtle">
              {fmtDate(event.event_date, language)}
            </p>
            <p className="mt-0.5 font-serif text-[13px] leading-snug text-ink-soft">
              {typeLabel === typeKey ? event.event_type : typeLabel}
            </p>
            {actorLabel !== actorKey && (
              <p className="text-[10px] text-ink-muted">{actorLabel}</p>
            )}
          </li>
        );
      })}
    </ol>
  </div>
);

const Condition = ({ condition, t, language }) => (
  <div className="flex h-full flex-col">
    <ScreenTitle>{t("passportPreview.screens.condition")}</ScreenTitle>
    <p className="mt-5 text-[9px] tracking-[0.18em] uppercase text-ink-subtle">
      {t("passportPreview.condition.assessedOn")}
    </p>
    <p className="mt-0.5 font-serif text-[15px] text-ink-soft">
      {fmtDate(condition.assessed_at, language)}
    </p>
    <ul className="mt-5 flex flex-1 flex-col gap-2">
      {condition.tags.map((tag) => (
        <li
          key={tag.key}
          className="flex items-baseline justify-between gap-3 border border-ink/10 px-3 py-2"
        >
          <span className="text-[11px] text-ink-body">
            {t(`passportPreview.condition.tags.${tag.key}`)}
          </span>
          <span className="text-[10px] tracking-[0.14em] uppercase text-gold-ink">
            {t(`passportPreview.condition.states.${tag.state}`)}
          </span>
        </li>
      ))}
    </ul>
    <p className="mt-4 text-[10px] leading-relaxed text-ink-subtle">
      {t("passportPreview.condition.note")}
    </p>
  </div>
);

const Continuity = ({ continuity, t, tFmt }) => {
  const count = continuity.owner_count;
  const shown = Math.min(count, MAX_AVATARS);
  return (
    <div className="flex h-full flex-col">
      <ScreenTitle>{t("passportPreview.screens.continuity")}</ScreenTitle>
      <div className="mt-6 flex flex-wrap gap-2">
        {Array.from({ length: shown }).map((_, i) => (
          <span
            key={i}
            aria-hidden="true"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 bg-surface-raised text-gold-ink"
          >
            <User size={15} strokeWidth={1.5} />
          </span>
        ))}
        {count > MAX_AVATARS && (
          <span className="flex h-9 items-center text-[11px] text-ink-subtle">
            +{count - MAX_AVATARS}
          </span>
        )}
      </div>
      <p className="mt-5 font-serif text-[17px] leading-snug text-ink-soft">
        {tFmt("passportPreview.continuity.owners", { count })}
      </p>
      <p className="mt-2 text-[10px] leading-relaxed text-ink-subtle">
        {t("passportPreview.continuity.anonymous")}
      </p>
      <p className="mt-auto border-t border-gold/20 pt-4 text-[12px] leading-relaxed text-gold-ink">
        {t("passportPreview.continuity.closing")}
      </p>
    </div>
  );
};

/**
 * Animated phone mockup showing what a Provena passport holds: identity,
 * chain of custody, condition report, continuity of ownership.
 *
 * Entirely driven by `passport` — no copy or fixture lives here, so a real
 * dossier can be passed in later untouched. A screen whose data is missing is
 * skipped. Auto-advance pauses on hover/focus and is disabled under
 * prefers-reduced-motion.
 *
 * @param {object} passport  { identity, custody[], condition, continuity }
 * @param {number} autoPlayMs 0 disables auto-advance
 */
export default function PassportPreview({
  passport,
  autoPlayMs = 3800,
  initialScreen = 0,
  testId = "passport-preview",
}) {
  const { t, tFmt, language } = useLanguage();
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(initialScreen);
  const [paused, setPaused] = useState(false);

  const screens = useMemo(
    () =>
      SCREEN_IDS.filter((id) => {
        if (id === "identity") return Boolean(passport?.identity);
        if (id === "custody") return Boolean(passport?.custody?.length);
        if (id === "condition") return Boolean(passport?.condition?.tags?.length);
        if (id === "continuity") return Boolean(passport?.continuity?.owner_count);
        return false;
      }),
    [passport]
  );

  const total = screens.length;
  const current = total ? screens[index % total] : null;

  useEffect(() => {
    if (!autoPlayMs || paused || reduce || total < 2) return undefined;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % total),
      autoPlayMs
    );
    return () => clearInterval(id);
  }, [autoPlayMs, paused, reduce, total]);

  if (!current) return null;

  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {/* Phone shell — the only rounded surface here, because it depicts a
          physical object; everything inside keeps the landing's square edges. */}
      <div className="relative w-[272px] rounded-[2rem] border border-gold/30 bg-surface p-3 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] sm:w-[292px]">
        <span
          aria-hidden="true"
          className="absolute left-1/2 top-3 h-1 w-16 -translate-x-1/2 rounded-full bg-ink/10"
        />
        <div
          className="relative mt-5 aspect-[9/16] overflow-hidden bg-surface-muted px-4 py-5"
          aria-live="polite"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              data-testid={`${testId}-screen-${current}`}
              className="h-full"
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, y: -12 }}
              transition={{ duration: reduce ? 0.001 : 0.45, ease: EASE_LUXE }}
            >
              {current === "identity" && (
                <Identity identity={passport.identity} t={t} />
              )}
              {current === "custody" && (
                <Custody custody={passport.custody} t={t} language={language} />
              )}
              {current === "condition" && (
                <Condition
                  condition={passport.condition}
                  t={t}
                  language={language}
                />
              )}
              {current === "continuity" && (
                <Continuity
                  continuity={passport.continuity}
                  t={t}
                  tFmt={tFmt}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {total > 1 && (
        // Le repère reste un trait fin ; c'est la zone tactile qui grandit.
        // Mesurées sur un téléphone, ces pastilles faisaient 6 x 6 pixels :
        // impossible à viser au doigt. Le rembourrage est transparent, porte le
        // clic, et n'ajoute rien à l'écran. L'espace entre les traits passe de
        // dix à vingt pixels, seule trace visible du changement.
        <div className="mt-6 flex items-center gap-0">
          {screens.map((id, i) => {
            const active = i === index % total;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={tFmt("passportPreview.nav.goTo", {
                  screen: t(`passportPreview.screens.${id}`),
                })}
                aria-current={active ? "true" : undefined}
                data-testid={`${testId}-dot-${id}`}
                className="group flex items-center px-2.5 py-5"
              >
                <span
                  aria-hidden="true"
                  className={`block h-1.5 transition-all duration-300 ${
                    active
                      ? "w-6 bg-gold"
                      : "w-1.5 bg-ink/20 group-hover:bg-ink/40"
                  }`}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
